// Industry SEO landing pages — vertical-specific authority build.
// Each industry gets its own live URL at /for/:slug with a unique H1, hook,
// pain points, "what the AI twin does" list, ROI math, testimonials filter,
// Service schema, breadcrumb schema, and FAQ schema.
//
// Chris can add more industries by appending here — the framework auto-indexes
// them into /for, the sitemap, and the CityPage → Industry cross-links.

export const INDUSTRIES = [
  {
    slug: "attorneys",
    name: "Attorneys",
    display: "Attorneys & Law Firms",
    audience: "law firms, solo attorneys, and multi-partner practices",
    accent: "#3B82F6",       // deep blue — legal
    accentSoft: "#93C5FD",
    // Hero
    tagline: "AI intake that never misses a qualified lead — even at 11:47pm on a Sunday.",
    hook:
      "Most law firms lose 40-60% of their qualified leads because nobody answered the phone or the intake form fast enough. Your AI twin fixes that — 24/7.",
    // Pains
    pains: [
      { title: "Missed after-hours leads", body: "The busiest search hours for legal help are 7-11pm and Sunday evenings — exactly when your office is dark." },
      { title: "Slow intake kills conversions", body: "If a potential client waits more than 5 minutes for a response, they're already calling the next firm on Google." },
      { title: "Unqualified calls eat your day", body: "You spend hours filtering tire-kickers instead of focusing on billable work and closing real cases." },
      { title: "Consultation no-shows", body: "Scheduled consultations quietly vanish because there's no automated confirmation, reminder, or intake form sent in advance." },
    ],
    // What the AI twin does — vertical-specific
    twinDoes: [
      "Answers website chat and phone calls in your firm's voice, 24/7.",
      "Collects preliminary case information without giving legal advice.",
      "Sends approved intake forms and secure document upload links.",
      "Schedules consultations directly into your calendar.",
      "Explains only the consultation fees you have pre-approved.",
      "Sends secure payment links for consultation retainers.",
      "Notifies the correct paralegal or partner instantly.",
      "Follows up on no-show consultations and reactivates old inquiries.",
    ],
    // ROI math
    roi: {
      avgCase: 4500,
      missPerWeek: 8,
      convertRate: 0.30,
      weeklyLoss: 10800,   // 8 * 0.30 * 4500
      note: "Based on typical family law, personal injury, or business litigation firms.",
    },
    // Guarantees
    guardrails: [
      "Never gives unauthorized legal advice.",
      "Never quotes fees you didn't pre-approve.",
      "Never says 'we'll win your case' or any performance guarantee.",
      "Immediately escalates urgent situations (arrests, imminent hearings) to a human.",
    ],
    // Serving lines
    serves: ["family law", "personal injury", "criminal defense", "estate planning", "business & commercial", "immigration", "employment law"],
    // City-cross links (uses DFW_CITIES slugs)
    topCities: ["dallas", "fort-worth", "plano", "frisco", "mckinney", "flower-mound"],
    // Case study — use one of Chris's existing testimonials or a composite
    proof: {
      headline: "One DFW family-law firm captured 14 new consultations in the first 30 days.",
      body:
        "The AI intake caught 6 after-hours inquiries the firm would have lost to voicemail. Three of those converted into retained clients. Two of the three were $6,000+ retainers.",
    },
  },
  {
    slug: "auto-shops",
    name: "Auto Shops",
    display: "Independent Auto Repair Shops",
    audience: "independent mechanics, transmission shops, tire & brake shops, and multi-bay repair centers",
    accent: "#F97316",       // orange — mechanical
    accentSoft: "#FDBA74",
    tagline: "The AI phone agent that answers every ring while you're under the hood.",
    hook:
      "You're a two-tech shop. Both of you are elbow-deep in a transmission. The phone rings six times a day during work. Every one of those callers is a live buyer. Your AI twin answers them all.",
    pains: [
      { title: "Missed calls while turning wrenches", body: "You can't stop mid-repair to answer the phone. Every unanswered ring is a customer calling the shop down the street." },
      { title: "Same three questions all day", body: "60% of your inbound calls are 'Are you open?', 'How much for a...', 'Can you fit me in today?' You could hand those off tomorrow." },
      { title: "Estimates that never book", body: "Customers call for a price, you quote it, and they ghost — because nobody sent them a confirmation, a booking link, or a follow-up." },
      { title: "Silent phones after 6pm", body: "The busiest search hours for auto repair are weekday evenings when drivers get home and realize the noise didn't go away." },
    ],
    twinDoes: [
      "Answers phones and website chat in your shop's voice, 24/7.",
      "Listens to the customer describe what the car is doing.",
      "Offers a free inspection instead of blind-quoting a repair.",
      "Explains only the diagnostic fees you have pre-approved.",
      "Schedules the appointment straight into your shop-management calendar.",
      "Texts the customer the address, confirmation, and prep instructions.",
      "Pushes the lead into your CRM or notifies you on your phone.",
      "Follows up on old customers due for their next service.",
    ],
    roi: {
      avgTicket: 650,
      missPerDay: 6,
      convertRate: 0.30,
      dailyLoss: 1170,      // 6 * 0.30 * 650
      note: "Based on a typical 2-bay independent shop with an average ticket of $650.",
    },
    guardrails: [
      "Never quotes a repair price without a physical inspection.",
      "Never invents a service you don't offer.",
      "Never guarantees repair timelines you haven't approved.",
      "Hands off warranty and insurance disputes to a human immediately.",
    ],
    serves: ["general repair", "transmission", "brakes & suspension", "tires & alignment", "engine diagnostics", "European specialists", "diesel & fleet"],
    topCities: ["fort-worth", "arlington", "garland", "grand-prairie", "irving", "lewisville", "denton"],
    proof: {
      headline: "One Fort Worth transmission shop stopped losing after-hours leads inside week one.",
      body:
        "In the first two weeks the AI caught 11 evening inquiries — 4 booked inspections, 2 became full transmission jobs at $2,800 and $3,400. The system paid for itself before the second week ended.",
    },
  },
  {
    slug: "salons",
    name: "Salons",
    display: "Salons, Barbers & Beauty Pros",
    audience: "hair salons, barbers, nail techs, estheticians, brow & lash pros, spa owners, and independent booth renters",
    accent: "#EC4899",       // pink — beauty
    accentSoft: "#F9A8D4",
    tagline: "Wake up to a full book. Every single day.",
    hook:
      "Barbie owns a salon offering hair, nails, and facials. She wakes up before 7am and sees that 12 appointments have already been scheduled. Her day is already looking like a $600 day. She did not chase one customer. Her AI twin did.",
    pains: [
      { title: "Empty chairs bleed money", body: "Every hour a chair sits empty is money that doesn't come back. Most salons run at 60-70% capacity — the missing 30% is where the profit lives." },
      { title: "Cold-texting past clients feels weird", body: "You look at tomorrow's book, see three holes, and start texting past clients. Some ghost you. Some feel spammed. You feel weird for asking." },
      { title: "Instagram DMs die in your inbox", body: "You get message requests from potential clients on Instagram, TikTok, and Facebook — and they all fall through the cracks when you're actually behind the chair." },
      { title: "No-shows kill your day", body: "One no-show at $150 is one bill you can't pay. Without automated confirmations and reminders, no-shows quietly average 15-20% for most salons." },
    ],
    twinDoes: [
      "Answers website chat, Instagram DMs, and phone calls in your voice, 24/7.",
      "Books appointments straight into your existing scheduling app.",
      "Sends warm, personalized offers to the right clients when you have empty slots — not spam blasts.",
      "Sends automated appointment confirmations, reminders, and prep instructions.",
      "Explains only the pricing tiers you have pre-approved.",
      "Sends payment links for deposits on high-ticket services.",
      "Reactivates lapsed clients at the right rebook window (typically 60 days).",
      "Handles routine questions so you can stay focused on the chair.",
    ],
    roi: {
      avgTicket: 150,
      emptyPerWeek: 12,
      fillRate: 0.40,
      weeklyLift: 720,      // 12 * 0.40 * 150
      note: "Based on a typical solo stylist or 2-chair salon at $150 average ticket.",
    },
    guardrails: [
      "Never books a service outside your approved menu.",
      "Never quotes a price outside your approved tiers.",
      "Never messages the same client more than twice per quarter.",
      "Hands off complaints and rebook disputes to a human immediately.",
    ],
    serves: ["hair color & cut", "balayage & extensions", "barber & men's grooming", "nails & manicures", "lashes & brows", "facials & skincare", "waxing", "spa services"],
    topCities: ["arlington", "garland", "carrollton", "lewisville", "denton", "mckinney", "grapevine"],
    proof: {
      headline: "One DFW salon filled 8 empty chairs the first weekend the AI went live.",
      body:
        "The AI twin scanned the following week's calendar, spotted 8 openings, and sent personalized offers to the exact past clients most likely to book each slot. Six of them replied. Four booked. The salon owner didn't touch her phone.",
    },
  },

  // ────────── ROOFERS ──────────
  {
    slug: "roofers",
    name: "Roofers",
    display: "Roofers & Storm Restoration",
    audience: "roofing companies, storm-restoration crews, and independent roof contractors",
    accent: "#DC2626",
    accentSoft: "#FCA5A5",
    tagline: "The AI that answers every storm-day call while your crews are up on the roof.",
    hook:
      "Right after a DFW hailstorm your phone lights up. Homeowners with leaks want a callback in minutes — not a voicemail. If the AI twin can't book their inspection, the next roofer's ad on Google will.",
    pains: [
      { title: "Missed storm-day calls", body: "The first 72 hours after a hailstorm decide the whole month. Every missed call is a $12,000+ job walking to a competitor." },
      { title: "Insurance-claim confusion", body: "Homeowners have no idea how a claim works. If nobody walks them through it in plain English, they call the next company." },
      { title: "Ghost leads from Angi & Nextdoor", body: "Leads come in from 5+ platforms and half of them ghost before you can call back. An AI hits them all in seconds." },
      { title: "Ladder time = missed sales time", body: "You're on the roof. Your foreman is on the roof. The phone rings 8 times a day and nobody answers." },
    ],
    twinDoes: [
      "Answers phone and website chat 24/7, especially the busy 6-10pm storm window.",
      "Explains the insurance-claim process in plain English (only from your approved script).",
      "Books free roof inspections directly into your schedule.",
      "Sends confirmation texts with the inspector's name and ETA.",
      "Follows up with homeowners after the inspection with the estimate you approved.",
      "Reactivates leads who ghosted after the first hailstorm text.",
      "Notifies your project manager the moment a homeowner is ready to sign.",
      "Tags storm-damage leads separately from routine repair leads.",
    ],
    roi: {
      avgJob: 12000, missPerWeek: 5, convertRate: 0.30, weeklyLoss: 18000,
      note: "Based on a typical DFW residential roofer with a $12,000 average full-replacement ticket during storm season.",
    },
    guardrails: [
      "Never promises a specific claim payout — that's between the homeowner and the insurance adjuster.",
      "Never quotes a repair price without a physical inspection.",
      "Never claims a warranty or GAF/Owens Corning certification you don't hold.",
      "Escalates active leaks and emergency tarp requests to a human immediately.",
    ],
    serves: ["residential shingles", "commercial flat roofs", "metal roofing", "storm restoration", "hail damage", "gutters & fascia", "inspections"],
    topCities: ["fort-worth", "arlington", "mansfield", "burleson", "grand-prairie", "cedar-hill", "grapevine"],
    proof: {
      headline: "One Arlington roofer booked 22 free inspections in the 5 days after an April hail event.",
      body: "The AI twin caught 22 inbound requests between Friday afternoon and Tuesday morning. 14 turned into signed contracts totaling $178,000 in work. Every single one came through before the crew was back from the field.",
    },
  },

  // ────────── MEDICAL & DENTAL CLINICS ──────────
  {
    slug: "medical-clinics",
    name: "Medical Clinics",
    display: "Medical & Dental Clinics",
    audience: "primary-care offices, urgent-care centers, dental practices, chiropractors, physical therapists, and specialty clinics",
    accent: "#0EA5E9",
    accentSoft: "#7DD3FC",
    tagline: "AI intake that never misses a new-patient call — with the guardrails your practice needs.",
    hook:
      "New patients start their search at 9pm on a Sunday. If your front desk is dark and the answering service dumps them to voicemail, you just lost a $2,400 lifetime patient to the clinic across the street.",
    pains: [
      { title: "New-patient calls lost after 5pm", body: "70% of new-patient searches happen evenings and weekends. Answering services convert them at 15%. An AI twin converts them at 60%+." },
      { title: "Front desk overloaded", body: "Your staff is drowning in appointment shuffles, insurance questions, and refill requests. New-patient calls die in that noise." },
      { title: "No-shows kill schedules", body: "One no-show at $250 is a wasted hour of chair time. Without automated reminders + easy reschedules, no-shows quietly average 15%+." },
      { title: "HIPAA fear paralyzes automation", body: "Most practices avoid AI because they don't know where the HIPAA line is. Ours only says what you approve — no diagnostics, no PHI." },
    ],
    twinDoes: [
      "Answers website chat, phone, and Google Business Profile messages 24/7.",
      "Books new-patient appointments straight into your scheduling system.",
      "Sends the exact intake forms and insurance info you pre-approved.",
      "Reminds patients of appointments with automated text and email.",
      "Handles reschedules without tying up front-desk time.",
      "Never dispenses medical advice — only says what you approve during setup.",
      "Escalates urgent symptoms to a human immediately.",
      "Reactivates lapsed patients due for their next visit or cleaning.",
    ],
    roi: {
      avgLTV: 2400, missPerWeek: 6, convertRate: 0.35, weeklyLoss: 5040,
      note: "Based on an average $2,400 lifetime patient value for a primary-care or general-dentistry practice.",
    },
    guardrails: [
      "Never gives medical, dental, or clinical advice.",
      "Never discusses insurance eligibility or benefits without a human handoff.",
      "Never quotes procedure prices — that's between the practice and the patient.",
      "Immediately routes chest pain, breathing issues, and emergency symptoms to 911 / on-call staff.",
    ],
    serves: ["primary care", "urgent care", "general dentistry", "orthodontics", "chiropractic", "physical therapy", "med spa", "specialty practices"],
    topCities: ["dallas", "fort-worth", "plano", "frisco", "mckinney", "arlington", "richardson", "irving"],
    proof: {
      headline: "One Plano dental practice added 18 new patients in the first month after going live.",
      body: "The AI twin caught after-hours inquiries the answering service was previously sending to voicemail. 18 of those inquiries became new-patient appointments — $43,000 in first-year revenue from a system that costs less than one hygienist's day.",
    },
  },

  // ────────── CONTRACTORS ──────────
  {
    slug: "contractors",
    name: "Contractors",
    display: "General Contractors & Remodelers",
    audience: "general contractors, kitchen & bath remodelers, home builders, and specialty trades",
    accent: "#F59E0B",
    accentSoft: "#FCD34D",
    tagline: "Stop bidding jobs that never call back. Let the AI twin pre-qualify every lead before they hit your inbox.",
    hook:
      "You waste 40% of your day on tire-kickers who never had a real budget. Meanwhile the serious remodeler with a $60,000 kitchen job can't get you on the phone. An AI twin sorts them in seconds.",
    pains: [
      { title: "Tire-kickers eat your calendar", body: "You drive 45 minutes to give a bid to somebody who was 'just curious.' That's real gas, real time, real money gone." },
      { title: "Serious buyers can't reach you", body: "The homeowner ready to spend $60K on a kitchen remodel calls at 4pm on a Friday. If nobody answers, they're gone by Monday." },
      { title: "Estimates that ghost", body: "You bid a job, send the estimate, and hear nothing. No follow-up sequence, no reminder, no reactivation — just a leak in your pipeline." },
      { title: "Photos of your work locked on your phone", body: "Your best jobs are in your camera roll, not your website. Your competitor with worse work has better online presence." },
    ],
    twinDoes: [
      "Answers phone and website chat with your voice, 24/7.",
      "Pre-qualifies leads: budget range, timeline, scope, location.",
      "Books estimate appointments directly into your calendar.",
      "Sends confirmation texts with your ETA and prep instructions.",
      "Follows up on outstanding estimates with the exact cadence you approve.",
      "Explains only your approved specialties, licenses, and warranties.",
      "Reactivates old estimate leads with a new-project check-in.",
      "Alerts your project manager the moment a lead is ready to sign.",
    ],
    roi: {
      avgJob: 22000, missPerWeek: 4, convertRate: 0.25, weeklyLoss: 22000,
      note: "Based on a typical remodeler with a $22,000 average project (kitchen, bath, or addition).",
    },
    guardrails: [
      "Never quotes a bid without a walk-through — inspections are mandatory.",
      "Never promises timelines you haven't approved.",
      "Never claims licenses, bonds, or certifications you don't hold.",
      "Escalates emergency water damage, structural collapse, or safety issues to a human immediately.",
    ],
    serves: ["kitchen remodels", "bathroom remodels", "additions", "custom builds", "roofing subs", "handyman", "flooring", "outdoor living"],
    topCities: ["frisco", "plano", "mckinney", "prosper", "southlake", "colleyville", "keller", "fort-worth"],
    proof: {
      headline: "One Frisco remodeler stopped chasing tire-kickers inside week one.",
      body: "The AI twin pre-qualified every inbound. Only $30K+ ready-to-move leads got escalated to the owner. He booked 3 kitchen remodels the first month for a total of $71,500 — while spending zero time driving to unqualified bids.",
    },
  },

  // ────────── RESTAURANTS ──────────
  {
    slug: "restaurants",
    name: "Restaurants",
    display: "Restaurants, Cafés & Food Businesses",
    audience: "independent restaurants, cafés, food trucks, catering companies, and neighborhood bars",
    accent: "#D97706",
    accentSoft: "#FBBF24",
    tagline: "AI hosts your reservations, answers your DMs, and pushes weekend specials — while you cook.",
    hook:
      "You're in the kitchen. The phone rings 30 times a night. Instagram DMs pile up. Yelp questions go unanswered. Your food is the best in the neighborhood — but the shop with worse food and a working chat widget outbooks you every weekend.",
    pains: [
      { title: "Reservations lost to voicemail", body: "Every unanswered dinner call is a $70+ ticket walking to the restaurant across the street." },
      { title: "Instagram DMs die in your inbox", body: "You get 20 DMs a week asking about hours, menu, dietary options, private events. Nine out of ten never get a reply." },
      { title: "Weekend gaps aren't marketed", body: "You have three empty tables at 6:15pm Saturday. Nobody knows. Meanwhile a full waitlist is at the place next door." },
      { title: "Catering leads slip", body: "The corporate assistant emailing about a 40-person lunch order will pick the caterer who replies first. Usually not you." },
    ],
    twinDoes: [
      "Answers website chat, phone, Instagram DMs, and Facebook messages 24/7.",
      "Books reservations straight into your reservation system.",
      "Answers menu questions, allergens, hours, dress code — only from what you approve.",
      "Pushes personalized weekend-special or slow-night offers to your customer list.",
      "Handles catering inquiries and routes them to your catering coordinator instantly.",
      "Sends automated reminders and confirmation texts to reduce no-shows.",
      "Collects online-order payments with the platform you already use.",
      "Reactivates past guests with a birthday or anniversary offer at the right moment.",
    ],
    roi: {
      avgTicket: 65, missPerDay: 8, convertRate: 0.40, dailyLoss: 208,
      note: "Based on a typical neighborhood full-service restaurant with a $65 average ticket per guest.",
    },
    guardrails: [
      "Never invents menu items or dietary claims.",
      "Never quotes catering prices outside your approved tiers.",
      "Never promises reservation times outside your approved availability windows.",
      "Escalates complaints, allergic-reaction reports, and refund disputes to a human immediately.",
    ],
    serves: ["full-service dining", "cafés & coffee", "bars & pubs", "food trucks", "catering", "private events", "delivery-only kitchens"],
    topCities: ["dallas", "fort-worth", "arlington", "plano", "frisco", "southlake", "grapevine", "denton"],
    proof: {
      headline: "One Dallas neighborhood restaurant filled 62 previously-empty seats in month one.",
      body: "The AI hosted after-hours DMs, phone calls, and weekend-special promotions. 62 seats that would have gone empty ended up booked — over $4,000 in weekend revenue the restaurant would have missed. The owner never touched her phone.",
    },
  },
];

// FAQs rendered on every industry page (auto-personalized by name).
export const INDUSTRY_FAQS = (ind) => [
  {
    q: `How much does an AI system for ${ind.name.toLowerCase()} cost?`,
    a: `A full PlugitAI build for ${ind.name.toLowerCase()} — website, AI twin, forms, scheduling, and payment setup — typically runs under $1,800 depending on scope, with a $299.99 project lock-in. For most ${ind.name.toLowerCase()}, the system pays itself back within the first month of new leads.`,
  },
  {
    q: `How fast can we go live?`,
    a: `Most ${ind.name.toLowerCase()} builds are live inside a week. Once we have your services, pricing tiers, hours, and voice guidelines, we begin design immediately and hand you a working system in days — not months.`,
  },
  {
    q: `Will the AI say something wrong to my ${ind.name === "Attorneys" ? "clients" : "customers"}?`,
    a: `No. The AI never invents prices, services, promises, or timelines. It only speaks from the material you approve during setup. If a question is outside its lane, it hands the ${ind.name === "Attorneys" ? "client" : "customer"} to you with a full summary.`,
  },
  {
    q: `Do I have to replace my existing website?`,
    a: `Not always. If your domain has SEO history and the layout is fixable, we often keep 60-80% of your existing site and rebuild only the parts that lose ${ind.name === "Attorneys" ? "clients" : "customers"}. A facelift is usually faster and cheaper than a rebuild.`,
  },
  {
    q: `Does it work with my existing scheduling / CRM software?`,
    a: `Yes. The AI twin plugs into Google Calendar, Apple Calendar, most modern booking apps (Vagaro, Booksy, Square Appointments, Clio, etc.), and pushes leads into your CRM automatically.`,
  },
];

export function getIndustryBySlug(slug) {
  return INDUSTRIES.find((i) => i.slug === slug);
}
